import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Button, Box, FormControl, InputLabel, Select, MenuItem, Grid, Typography, Divider, Chip } from '@mui/material';
import { ProjectActivity, Activity } from '../types/ActivityTypes';
import { Project } from '../types/projectTypes';
import { ProjectStatistics } from '../types/projectStatisticsType';
import { getStatistics } from '../api/projectApi';
import { classifyActivity, computeActivityGoals, computeRoadAdvance, GoalField } from '../utils/activityGoals';

interface ProjectActivityFormProps {
  onSubmit: (activity: ProjectActivity) => void;
  activities: Activity[];
  project: Project;
}

const emptyAuto: Record<GoalField, boolean> = {
  productionGoal: false, performacePo: false, performaceHmPo: false,
  performaceUnitGoal: false, performanceUnitPo: false, performanceUnitHmPo: false,
};

const CATEGORY_LABEL: Record<string, string> = {
  INDEPENDIENTE: 'Independiente (manual)',
  CAMINOS: 'Caminos',
  OBRAS_CIVILES: 'Obras Civiles',
  MONTAJE: 'Montaje',
  TENDIDO_CONDUCTOR: 'Tendido Conductor',
  TENDIDO_OPGW: 'Tendido OPGW',
};

const ProjectActivityForm: React.FC<ProjectActivityFormProps> = ({ onSubmit, activities, project }) => {
  const [isActivitySelected, setIsActivitySelected] = useState(false);
  const [stats, setStats] = useState<ProjectStatistics | null>(null);
  const [autoFields, setAutoFields] = useState<Record<GoalField, boolean>>(emptyAuto);

  const blankActivity = (): ProjectActivity => ({
    id: 0,
    project,
    activity: activities[0],
    performacePo: 0,
    productionGoal: 0,
    weightDistribution: 0,
    performaceUnitGoal: 0,
    performaceHmPo: 0,
    performanceUnitPo: 0,
    performanceUnitHmPo: 0,
    activityIdDependency: 0,
  });

  const [newActivity, setNewActivity] = useState<ProjectActivity>(blankActivity());

  const isAddProduction = newActivity.activity?.addProduction ?? false;
  const isAddMh = newActivity.activity?.addMh ?? false;
  const category = classifyActivity(newActivity.activity);

  // Trae las estadísticas del proyecto (ratios de conversión) una vez.
  useEffect(() => {
    if (project.id) {
      getStatistics(project.id)
        .then(setStats)
        .catch((e) => console.warn('No se pudieron cargar las estadísticas del proyecto (ratios):', e));
    }
  }, [project.id]);

  /**
   * Recalcula las metas automáticas a partir de la actividad, el peso, los objetivos
   * de fase del proyecto y los ratios. Devuelve la actividad con los valores aplicados.
   */
  const recalc = useCallback((act: ProjectActivity, statsData: ProjectStatistics | null): ProjectActivity => {
    if (!act.activity || !statsData) {
      setAutoFields(emptyAuto);
      return act;
    }
    const { values, auto } = computeActivityGoals(act.activity, Number(act.weightDistribution || 0), project, statsData);
    let merged: ProjectActivity = { ...act, ...values };

    if (classifyActivity(act.activity) === 'CAMINOS') {
      const adv = computeRoadAdvance(
        act.activity,
        { productionGoal: merged.productionGoal, performacePo: merged.performacePo, performaceHmPo: merged.performaceHmPo },
        statsData,
      );
      merged = { ...merged, ...adv };
    }

    setAutoFields(auto);
    return merged;
  }, [project]);

  // Recalcula cuando llegan las estadísticas (o cambia el proyecto).
  useEffect(() => {
    setNewActivity((prev) => recalc(prev, stats));
  }, [stats, recalc]);

  const handleActivityChange = (activityId: number) => {
    const selectedActivity = activities.find((activity) => activity.id === activityId);
    setIsActivitySelected(!!selectedActivity);
    if (selectedActivity) {
      // Al cambiar de actividad reiniciamos las metas y recalculamos las automáticas.
      const base: ProjectActivity = {
        ...newActivity,
        activity: selectedActivity,
        productionGoal: 0, performacePo: 0, performaceHmPo: 0,
        performaceUnitGoal: 0, performanceUnitPo: 0, performanceUnitHmPo: 0,
      };
      setNewActivity(recalc(base, stats));
    }
  };

  // Cambio de un campo MANUAL. Si es Caminos y cambia H/I/J, recalcula K/L/M.
  const handleManual = <K extends keyof ProjectActivity>(field: K, raw: string) => {
    const value = (raw === '' ? 0 : parseFloat(raw)) as ProjectActivity[K];
    setNewActivity((prev) => {
      let next: ProjectActivity = { ...prev, [field]: (typeof value === 'number' && isNaN(value as any) ? 0 : value) };
      if (classifyActivity(next.activity) === 'CAMINOS' &&
          (field === 'productionGoal' || field === 'performacePo' || field === 'performaceHmPo')) {
        const adv = computeRoadAdvance(
          next.activity,
          { productionGoal: next.productionGoal, performacePo: next.performacePo, performaceHmPo: next.performaceHmPo },
          stats,
        );
        next = { ...next, ...adv };
      }
      return next;
    });
  };

  // Cambio del Peso: recalcula todas las metas automáticas de fase.
  const handleWeightChange = (raw: string) => {
    const value = raw === '' ? 0 : parseFloat(raw);
    setNewActivity((prev) => recalc({ ...prev, weightDistribution: isNaN(value) ? 0 : value }, stats));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...newActivity, project });
    setNewActivity(blankActivity());
    setAutoFields(emptyAuto);
    setIsActivitySelected(false);
  };

  const sectionTitleStyle = { fontWeight: 'bold', fontSize: '0.95rem', color: '#333', marginTop: 1, marginBottom: 0.5 };
  const val = (v?: number) => (v === undefined || v === null || v === 0 ? '' : v);
  const autoHelp = (isAuto: boolean) => (isAuto ? 'Automático' : undefined);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, mb: 2, backgroundColor: '#fafafa' }}>
      <Grid container spacing={2}>

        {/* Selección de Actividad */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel>Actividad</InputLabel>
            <Select
              value={newActivity.activity?.id || ''}
              label="Actividad"
              onChange={(e) => handleActivityChange(Number(e.target.value))}
            >
              {activities.map((activity) => (
                <MenuItem key={activity.id} value={activity.id}>
                  {activity.typology} - ({activity.section}) - {activity.name} - {activity.subActivity}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel>Actividad de la que depende (Actividad anterior)</InputLabel>
            <Select value={newActivity.activityIdDependency || ''}
              label="Actividad de la que depende (Actividad anterior)"
              onChange={(e) => setNewActivity({ ...newActivity, activityIdDependency: Number(e.target.value) })} >
              <MenuItem value={0}><em>Ninguna</em></MenuItem>
              {activities.map((activity) => (
                <MenuItem key={activity.id} value={activity.id}>
                  {activity.typology} - ({activity.section}) - {activity.name} - {activity.subActivity}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Indicador de categoría detectada */}
        {isActivitySelected && (
          <Grid item xs={12}>
            <Chip
              size="small"
              label={`Categoría: ${CATEGORY_LABEL[category] || category}`}
              color={category === 'INDEPENDIENTE' ? 'default' : 'primary'}
              variant="outlined"
            />
            {category !== 'INDEPENDIENTE' && (
              <Typography component="span" sx={{ ml: 1, fontSize: '0.78rem', color: '#777' }}>
                Las metas marcadas “Automático” se calculan desde los objetivos del proyecto y los ratios de torres.
              </Typography>
            )}
          </Grid>
        )}

        {/* Producción P0 */}
        {isAddProduction && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={sectionTitleStyle}>Producción P0</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`Objetivo Producción (${newActivity.activity?.unitOfMeasurementProduction}/día)`}
                type="number" size="small" margin="dense" fullWidth
                value={val(newActivity.productionGoal)}
                onChange={(e) => handleManual('productionGoal', e.target.value)}
                disabled={autoFields.productionGoal}
                helperText={autoHelp(autoFields.productionGoal)}
              />
            </Grid>
            {newActivity.activity?.unitOfMeasurement !== 'NA' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={`Objetivo Avance (${newActivity.activity?.unitOfMeasurement})`}
                  type="number" size="small" margin="dense" fullWidth
                  value={val(newActivity.performaceUnitGoal)}
                  onChange={(e) => handleManual('performaceUnitGoal', e.target.value)}
                  disabled={autoFields.performaceUnitGoal}
                  helperText={autoHelp(autoFields.performaceUnitGoal)}
                />
              </Grid>
            )}
          </>
        )}

        {/* Rendimiento P0 (Horas Hombre) */}
        {isAddMh && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={sectionTitleStyle}>Rendimiento P0 (Horas Hombre)</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Rendimiento P0 (HH / Torres)"
                type="number" size="small" margin="dense" fullWidth
                value={val(newActivity.performacePo)}
                onChange={(e) => handleManual('performacePo', e.target.value)}
                disabled={autoFields.performacePo}
                helperText={autoHelp(autoFields.performacePo)}
              />
            </Grid>
            {newActivity.activity?.unitOfMeasurement !== 'NA' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={`Rendimiento P0 (HH / ${newActivity.activity?.unitOfMeasurement})`}
                  type="number" size="small" margin="dense" fullWidth
                  value={val(newActivity.performanceUnitPo)}
                  onChange={(e) => handleManual('performanceUnitPo', e.target.value)}
                  disabled={autoFields.performanceUnitPo}
                  helperText={autoHelp(autoFields.performanceUnitPo)}
                />
              </Grid>
            )}
          </>
        )}

        {/* Rendimiento P0 (Horas Máquina) */}
        {isAddMh && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography sx={sectionTitleStyle}>Rendimiento P0 (Horas Máquina)</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Rendimiento P0 (HM / Torres)"
                type="number" size="small" margin="dense" fullWidth
                value={val(newActivity.performaceHmPo)}
                onChange={(e) => handleManual('performaceHmPo', e.target.value)}
                disabled={autoFields.performaceHmPo}
                helperText={autoHelp(autoFields.performaceHmPo)}
              />
            </Grid>
            {newActivity.activity?.unitOfMeasurement !== 'NA' && (
              <Grid item xs={12} md={6}>
                <TextField
                  label={`Rendimiento P0 (HM / ${newActivity.activity?.unitOfMeasurement})`}
                  type="number" size="small" margin="dense" fullWidth
                  value={val(newActivity.performanceUnitHmPo)}
                  onChange={(e) => handleManual('performanceUnitHmPo', e.target.value)}
                  disabled={autoFields.performanceUnitHmPo}
                  helperText={autoHelp(autoFields.performanceUnitHmPo)}
                />
              </Grid>
            )}
          </>
        )}

        {/* Distribución del Peso */}
        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <TextField
            label="Distribución del Peso (%)"
            type="number" size="small" margin="dense" fullWidth
            value={val(newActivity.weightDistribution)}
            onChange={(e) => handleWeightChange(e.target.value)}
            InputProps={{ endAdornment: <span style={{ fontSize: '0.8rem' }}>%</span> }}
            helperText={category !== 'INDEPENDIENTE' && category !== 'CAMINOS' ? 'Distribuye el objetivo de la fase entre sus actividades (debe sumar 100% por fase)' : undefined}
          />
        </Grid>

        {/* Botón de Guardar */}
        <Grid item xs={12} container justifyContent="flex-end">
          <Button type="submit" variant="contained" color="primary" disabled={!isActivitySelected} size="small" sx={{ mt: 1 }}>
            Agregar Actividad
          </Button>
        </Grid>

      </Grid>
    </Box>
  );
};

export default ProjectActivityForm;
